import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Trace } from '@/features/traceability/type/Tool';
import { ReferenceComparisonLayout } from '@/features/analytics/components/compare/ReferenceComparisonLayout';
import { useComparisonToolsFetch } from '../hooks/useComparisonToolsFetch';
import { useReferenceSelection } from '../hooks/useReferenceSelection';

export function ReferenceComparisonPage() {
  const navigate = useNavigate();
  const { reference: urlReference } = useParams<{ reference?: string }>();

  const { tools, isLoading, handleRefresh } = useComparisonToolsFetch();

  // Extract comparison options from tools
  const comparisonOptions = useMemo(() => {
    const unique = new Set<string>();
    tools.forEach((tool) => {
      const normalized = typeof tool.reference === 'string' ? tool.reference.trim() : '';
      if (normalized.length) {
        unique.add(normalized);
      }
    });
    return Array.from(unique.values());
  }, [tools]);

  const {
    selectedReferences,
    referenceContext,
    handlePrimarySelect,
    handleToggleReference,
    getResetRefsCallback,
  } = useReferenceSelection(comparisonOptions, urlReference);

  // Filter state
  const [selectedMachine, setSelectedMachine] = useState<string>('__all__');
  const [selectedMaterial, setSelectedMaterial] = useState<string>('__all__');
  const [selectedOperation, setSelectedOperation] = useState<string>('__all__');

  // Extract filter options from traces
  const machineOptions = useMemo(() => {
    const unique = new Set<string>();
    tools.forEach((tool) => {
      const traces = Array.isArray(tool.traces) ? (tool.traces as Trace[]) : [];
      traces.forEach((trace) => {
        if (trace.machine?.name) {
          unique.add(trace.machine.name);
        }
      });
    });
    return Array.from(unique.values()).sort();
  }, [tools]);

  const materialOptions = useMemo(() => {
    const unique = new Set<string>();
    tools.forEach((tool) => {
      const traces = Array.isArray(tool.traces) ? (tool.traces as Trace[]) : [];
      traces.forEach((trace) => {
        if (trace.material?.name) {
          unique.add(trace.material.name);
        }
      });
    });
    return Array.from(unique.values()).sort();
  }, [tools]);

  const operationOptions = useMemo(() => {
    const unique = new Set<string>();
    tools.forEach((tool) => {
      const traces = Array.isArray(tool.traces) ? (tool.traces as Trace[]) : [];
      traces.forEach((trace) => {
        if (trace.operation) unique.add(trace.operation);
      });
    });
    return Array.from(unique).sort();
  }, [tools]);

  const handleClearFilters = useCallback(() => {
    setSelectedMachine('__all__');
    setSelectedMaterial('__all__');
    setSelectedOperation('__all__');
  }, []);

  const onRefresh = useCallback(() => {
    handleRefresh(getResetRefsCallback());
  }, [handleRefresh, getResetRefsCallback]);

  return (
    <ReferenceComparisonLayout
      tools={tools}
      comparisonOptions={comparisonOptions}
      selectedReferences={selectedReferences}
      selectedMachine={selectedMachine}
      selectedMaterial={selectedMaterial}
      selectedOperation={selectedOperation}
      machineOptions={machineOptions}
      materialOptions={materialOptions}
      operationOptions={operationOptions}
      isLoading={isLoading}
      isRefreshing={isLoading}
      referenceContext={referenceContext}
      onPrimarySelect={handlePrimarySelect}
      onToggleReference={handleToggleReference}
      onMachineChange={setSelectedMachine}
      onMaterialChange={setSelectedMaterial}
      onOperationChange={setSelectedOperation}
      onClearFilters={handleClearFilters}
      onRefresh={onRefresh}
      onNavigateBack={() => navigate('/analytics')}
      onNavigateHome={() => navigate('/')}
    />
  );
}
